// libs
import React, { useEffect, useMemo, useState, ErrorInfo, useCallback } from "react";

// constants
import { CACHE_POSTFIX } from "@swiggy-private/aatm-nirbhar-build/dist/constants/htmlCaching";

// components
import { ErrorPage } from "@swiggy-private/aatm-nirbhar-ui/dist/lib/ErrorPage";
import GlobalStyled from "@swiggy-private/aatm-nirbhar-ui/dist/lib/GlobalStyled";
import { NewRelicErrorBoundary } from "@swiggy-private/aatm-nirbhar-ui/dist/lib/NewRelic";

// dls
import { WidgetControllerContext } from "@swiggy-private/dls-react-mxn/dist";
import { BaseTheme } from "@swiggy-private/dls-web-common/dist/components/themes/Base";
import { InstamartTheme } from "@swiggy-private/dls-web-common/dist/components/themes/Instamart";
import { FontStyle } from "@swiggy-private/dls-web-theme/dist/components/Fonts";
import { ThemeProvider } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider";

// context
import { GenericContext } from "@swiggy-private/generic-mxn-context/dist";

// types
import { GetSwiggyHeadersResponsePayload } from "@swiggy-private/native-bridge/dist/interfaces";
import type { AppProps } from "next/app";

// helpers
import { NEW_RELIC_APPLICATION_ID } from "../constants";
import { isImShell } from "../helpers";
import { sendCustomNREvViaBlaze, sendErrorBoundaryEvent } from "../helpers/newrelic";
import { Sdk, useBootstrap } from "../hooks/useBootstrap";
import { ShuttleAppName } from "../styles/home";
import { getAnalyticsContextValue } from "../utils/analytics";
import { closeWebview, getSwiggyHeaders } from "../utils/bridgeSdk";

const CLOSE = "click here to close";

function MyApp({ Component, pageProps, router }: AppProps) {
    // Detect if the page is a cached version
    const isCached = router.route.includes(CACHE_POSTFIX);
    const [headers, setHeaders] = useState<GetSwiggyHeadersResponsePayload>();

    // True once getSwiggyHeaders has settled (success or failure). Lets consumers
    // distinguish "headers still pending" from "headers fetched but empty" — e.g. the
    // IRCTC page waiting to decide platform without hanging on a failed bridge call.
    const [headersResolved, setHeadersResolved] = useState(false);
    const contextValue = useMemo(() => {
        const analyticsContext = getAnalyticsContextValue(headers);
        return {
            sdk: Sdk ?? undefined,
            impressionAnalytics: analyticsContext.impressionEvent,
            clickAnalytics: analyticsContext.clickEvent,
            headers,
        };
    }, [headers]);

    useEffect(() => {
        async function fetchData() {
            try {
                if (headers) {
                    return;
                }
                const headersResponse = await getSwiggyHeaders();

                setHeaders(headersResponse);
            } catch (err) {
            } finally {
                setHeadersResolved(true);
            }
        }
        fetchData();
    }, [headers]);

    useBootstrap("portal_static_page", isCached, headers);

    const onError = useCallback(
        (error?: Error, errorInfo?: ErrorInfo) => {
            sendErrorBoundaryEvent(error, errorInfo, headers);
        },
        [headers],
    );

    const redirectToPath = useCallback(
        ({ pathname, query }: { pathname: string; query: { [key: string]: string | number | boolean } }) => {
            router.push({
                pathname,
                query: { ...router.query, ...query },
            });
        },
        [router],
    );

    const ErrorComponentWrapper = () => <ErrorPage onCtaClick={closeWebview} ctaText={CLOSE} />;

    const genericContextValue = useMemo(
        () => ({ headers, headersResolved, sendCustomNREvViaBlaze, redirectToPath }),
        [headers, headersResolved, redirectToPath],
    );

    const channel = headers?.["x-channel"];
    const appTheme = isImShell(channel) ? InstamartTheme : BaseTheme;

    return (
        <ThemeProvider theme={appTheme} colorMode="light">
            <NewRelicErrorBoundary
                applicationID={NEW_RELIC_APPLICATION_ID}
                onErrorCapture={onError}
                ErrorComponent={ErrorComponentWrapper}
            >
                <ShuttleAppName data-app-name={`${process.env.NEXT_PUBLIC_APP_NAME}`} />
                <GlobalStyled />
                <FontStyle />
                <GenericContext.Provider value={genericContextValue}>
                    <WidgetControllerContext.Provider value={contextValue}>
                        <Component {...pageProps} />
                    </WidgetControllerContext.Provider>
                </GenericContext.Provider>
            </NewRelicErrorBoundary>
        </ThemeProvider>
    );
}

export default MyApp;
